import React from 'react'
import styled from 'styled-components'
import { Form } from 'semantic-ui-react'
import { columnHeading } from './consolidated-report-email-utils'
import { groupKeyOf, templateSequence } from './email-content-utils'

// Laid out like the digest the server sends: one block per template, each with its own
// header row (standard columns + that template's fields, in the saved order). Sample values only.

const Frame = styled.div`
    background: #eef0f5;
    border-radius: 12px;
    padding: 14px;
`

const Mail = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    max-width: 980px;
    margin: 0 auto;

    .meta {
        padding: 10px 16px;
        border-bottom: 1px solid #e5e7eb;
        font-size: 12px;
        color: #6b7280;
    }
    .meta b {
        color: #111827;
    }
    .head {
        background: #1b2587;
        color: #fff;
        padding: 14px 16px;
    }
    .head b {
        display: block;
        font-size: 15px;
    }
    .head span {
        font-size: 12px;
        opacity: 0.85;
    }
    .body {
        padding: 12px;
        overflow-x: auto;
    }
    .blk {
        margin-bottom: 14px;
        width: max-content;
        min-width: 100%;
    }
    .blk-t {
        font-weight: 700;
        font-size: 13px;
        padding: 7px 10px;
        background: #eef1fd;
        border: 1px solid #dbe1fb;
        border-bottom: none;
        border-radius: 8px 8px 0 0;
        display: flex;
        justify-content: space-between;
        gap: 12px;
    }
    .blk-t span + span {
        font-weight: 400;
        color: #64748b;
    }
    table {
        border-collapse: collapse;
        width: 100%;
        font-size: 12px;
    }
    th {
        background: #f9fafb;
        text-align: left;
        padding: 7px 9px;
        border: 1px solid #e5e7eb;
        font-weight: 650;
        white-space: nowrap;
    }
    td {
        padding: 7px 9px;
        border: 1px solid #e5e7eb;
        vertical-align: top;
    }
    td .lnk {
        color: #263ba6;
        text-decoration: underline;
    }
    .thumbs {
        display: flex;
        gap: 3px;
        margin-top: 3px;
    }
    .thumbs i {
        width: 28px;
        height: 21px;
        border-radius: 3px;
        background: linear-gradient(135deg, #cbd5e1, #94a3b8);
        display: block;
    }
    .foot {
        padding: 10px 16px;
        border-top: 1px solid #e5e7eb;
        font-size: 11px;
        color: #6b7280;
    }
`

const SAMPLE_ROWS = [
    { audit: 'Sample audit 1', auditor: 'Auditor A', submitted: '28 Oct 2026', score: '92%', branch: 'Location 1' },
    { audit: 'Sample audit 2', auditor: 'Auditor B', submitted: '27 Oct 2026', score: '78%', branch: 'Location 2' },
]

const sampleAnswer = (column, row) => {
    const type = String(column.responseType || '').toLowerCase()
    if (type.includes('number')) return row === 0 ? '120' : '48'
    if (type.includes('date')) return row === 0 ? '27 Oct 2026' : '26 Oct 2026'
    if (type.includes('choice')) return row === 0 ? 'Option A' : 'Option B'
    return 'Sample'
}

const builtinValue = (key, row, templateName) => {
    const sample = SAMPLE_ROWS[row]
    switch (key) {
        case 'audit':
            return <span className="lnk">{sample.audit}</span>
        case 'link':
            return <span className="lnk">View</span>
        case 'template':
            return templateName
        default:
            return sample[key] || ''
    }
}

const isColumnInTable = column =>
    column.builtin ? column.locked || column.visible : column.visible !== false

const Block = ({ title, note, columns }) => (
    <div className="blk">
        <div className="blk-t">
            <span>{title}</span>
            <span>{note}</span>
        </div>
        <table>
            <thead>
                <tr>
                    {columns.map(column => (
                        <th key={column.key}>{columnHeading(column) || ' '}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {[0, 1].map(row => (
                    <tr key={row}>
                        {columns.map(column => (
                            <td key={column.key}>
                                {column.builtin
                                    ? builtinValue(column.key, row, title)
                                    : sampleAnswer(column, row)}
                                {!column.builtin && column.showPhotos && (
                                    <div className="thumbs">
                                        <i />
                                        <i />
                                    </div>
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

export const EmailPreviewSection = ({
    scheduleName,
    includeAllTemplates,
    templateIds = [],
    templateOrderSaved = false,
    columns = [],
    templateOptions = [],
    templateRecords = [],
    recipientCount = '',
}) => {
    const nameOf = key => {
        const option = (templateOptions || []).find(item => String(item.value) === String(key))
        return option?.text || `Template ${key}`
    }

    let keys = (templateIds || []).map(String)
    if (includeAllTemplates) {
        keys = []
        ;(columns || []).forEach(column => {
            const key = groupKeyOf(column, templateRecords)
            if (key && !keys.includes(key)) keys.push(key)
        })
    }
    const standardOnly = (columns || []).filter(
        column => column.builtin && isColumnInTable(column),
    )

    return (
        <Form.Field>
            <label>Email preview</label>
            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                How the email will look: one block per template
                {includeAllTemplates || !templateOrderSaved ? ' (the newest audit decides which comes first)' : ', in your order'},
                each with its own header row.
                Sample values; use &quot;Send test email now&quot; to see real data.
            </div>
            <Frame>
                <Mail>
                    <div className="meta">
                        <b>Subject:</b> {scheduleName || 'Consolidated report'}
                        <br />
                        <b>To:</b> {recipientCount || 'no recipients yet'}
                    </div>
                    <div className="head">
                        <b>{scheduleName || 'Consolidated report'}</b>
                        <span>Sample rows</span>
                    </div>
                    <div className="body">
                        {keys.map(key => (
                            <Block
                                key={key}
                                title={nameOf(key)}
                                note="2 reports"
                                columns={templateSequence(columns, key, templateRecords).filter(
                                    isColumnInTable,
                                )}
                            />
                        ))}
                        {includeAllTemplates && (
                            <Block
                                title="Other templates"
                                note="standard columns only"
                                columns={standardOnly}
                            />
                        )}
                        {!keys.length && !includeAllTemplates && (
                            <div style={{ color: '#6b7280', fontSize: 13 }}>
                                Pick a template to see the email.
                            </div>
                        )}
                    </div>
                    <div className="foot">
                        You receive this because you are on this schedule. Unsubscribe
                    </div>
                </Mail>
            </Frame>
        </Form.Field>
    )
}
